import React from 'react';
import { EditorialPost } from '../../../types/editorial';
import { Button } from '../../ui';
import { canApprovePost, formatBrasilia, isOverdue, statusLabels } from './editorialForm';
import { Field, Hint, Note, Panel, Row, Status } from './editorialStyles';
import { EditorialAction } from '../../../services/editorial';

interface Props {
  post: EditorialPost;
  dirty: boolean;
  busy: boolean;
  canApprove: boolean;
  note: string;
  onNote: (value: string) => void;
  onAction: (action: EditorialAction) => void;
}

const historyLabels: Record<string, string> = {
  created: 'Rascunho criado', updated: 'Artigo atualizado', submitted: 'Enviado para aprovação',
  approved: 'Aprovado e agendado', changes_requested: 'Ajustes solicitados', published: 'Publicado',
  archived: 'Programação arquivada', draft_created: 'Rascunho criado', submitted_for_review: 'Enviado para aprovação',
  publication_blocked: 'Publicação precisa de revisão', seeded: 'Artigo incluído na programação',
  imported: 'Artigo já publicado incluído na revisão',
};

const EditorialReview: React.FC<Props> = ({ post, dirty, busy, canApprove, note, onNote, onAction }) => {
  const pending = post.status === 'pending_review';
  return <Panel aria-labelledby="review-heading">
    <Row><h2 id="review-heading">Aprovação</h2><Status>{statusLabels[post.status]}</Status><Hint>Versão {post.revision}</Hint></Row>
    {dirty && <Note $warning>Há alterações não salvas. Salve o artigo antes de enviar ou aprovar. Uma alteração salva exige nova aprovação.</Note>}
    {isOverdue(post) && <Note $warning>Data vencida, publicação pendente. Defina uma nova data futura e envie novamente para aprovação.</Note>}
    {post.reviewer_note && <Note $warning><strong>Ajustes solicitados:</strong> {post.reviewer_note}</Note>}
    {post.approved_at && post.approved_revision === post.revision && <Note>
      Versão {post.approved_revision} aprovada por {post.approved_by_name || 'administrador'} em {formatBrasilia(post.approved_at)} (Brasília).
    </Note>}
    {['draft', 'changes_requested'].includes(post.status) && <Row>
      <Button type="button" disabled={busy || dirty} onClick={() => onAction('submit')}>Enviar para aprovação</Button>
    </Row>}
    {pending && !canApprove && <Hint>Aguardando a revisão de um administrador. O artigo só entra no site depois da aprovação.</Hint>}
    {pending && canApprove && <>
      <Hint>Ao aprovar, você autoriza a publicação da versão {post.revision} em {formatBrasilia(post.scheduled_at)} (Brasília).</Hint>
      <Row>
        <Button type="button" disabled={busy || !canApprovePost(post, canApprove, dirty)} onClick={() => onAction('approve')}>
          Aprovar e agendar
        </Button>
      </Row>
      {!post.scheduled_at && <Hint>Defina uma data futura, salve e envie novamente para aprovação.</Hint>}
      <Field>Motivo dos ajustes
        <textarea rows={3} value={note} maxLength={4000} disabled={busy} placeholder="Explique o que precisa ser corrigido antes da publicação."
          onChange={event => onNote(event.target.value)} />
      </Field>
      <Row><Button type="button" $variant="secondary" disabled={busy || dirty || note.trim().length < 3} onClick={() => onAction('request-changes')}>Solicitar ajustes</Button></Row>
    </>}
    {!['archived', 'published'].includes(post.status) && <Row>
      <Button type="button" $variant="secondary" disabled={busy || dirty} onClick={() => onAction('archive')}>Arquivar programação</Button>
      <Hint>Cancela a agenda deste artigo.</Hint>
    </Row>}
    {post.status === 'archived' && <Hint>Para retomar, edite e salve um novo rascunho.</Hint>}
    <details>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Histórico do artigo</summary>
      {post.history.length === 0 ? <Hint>As próximas ações aparecerão aqui.</Hint> : <ol style={{ paddingLeft: '1.3rem', marginTop: '.8rem' }}>
        {[...post.history].reverse().map((item, index) => <li key={`${item.at}-${index}`} style={{ marginBottom: '.8rem' }}>
          <strong>{historyLabels[item.action] || item.action}</strong> · {item.actor_name || 'Sistema'}
          <Hint>{formatBrasilia(item.at)} (Brasília)</Hint>
          {item.note && <p>{item.note}</p>}
        </li>)}
      </ol>}
    </details>
  </Panel>;
};

export default EditorialReview;
